import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { AgentLocationSummary, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import AgentLocationsContext from "../../contexts/AgentLocationsContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAgentLocations from "../../hooks/data/useAgentLocationSummaries";

function requiredAgentLocationId(row: AgentLocationSummary): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Agent location summary is missing its ID.");
    }

    return row.id;
}

const useAgentLocationColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<AgentLocationSummary>[] = [
            {
                id: "agentName",
                label: "Agent",
                scalarType: "text",
                accessor: (row) => row.agentName,
                flex: 1,
            },
            {
                id: "location",
                label: "Location",
                scalarType: "text",
                accessor: (row) => row.location,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 70,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/agent-locations/edit/${requiredAgentLocationId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const Agents: FC = () => {
    const columns = useAgentLocationColumns();

    const theme = useTheme();

    const context = useAgentLocations();

    const { data: agentLocations, loading: agentLocationsLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Agent Locations`,
            exportTitle: `Agent Locations`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <AgentLocationsContext.Provider value={context}>
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Outlet />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/agent-locations/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Agent Location
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Agent Locations"
                        loading={agentLocationsLoading}
                        rows={agentLocations ?? []}
                        columns={columns}
                        getRowId={requiredAgentLocationId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </AgentLocationsContext.Provider>
    );
};

export default Agents;
